import axios from 'axios'
import logger from '@/utils/logger'

/**
 * Fetch transactions with filtering and pagination
 * @param {Object} params - Query parameters including filters
 * @returns {Promise} - Promise that resolves to transactions data
 */
export async function fetchTransactions(params = {}) {
  try {
    const response = await axios.get('/transactions/', { params })
    return response.data
  } catch (error) {
    logger.error('Failed to fetch transactions:', error)

    if (error.response?.status === 401) {
      logger.warn('Authentication failed when fetching transactions')
      return { transactions: [], pagination: {} }
    }

    throw error
  }
}

/**
 * Fetch a single transaction by ID
 * @param {number} transactionId - The transaction ID
 * @param {Object} options - Additional options like include_related
 * @returns {Promise} - Promise that resolves to transaction data
 */
export async function fetchTransaction(transactionId, options = {}) {
  try {
    const params = {}
    if (options.includeRelated) {
      params.include = 'details'
    }

    const response = await axios.get(`/transactions/${transactionId}`, { params })
    return response.data.transaction || response.data
  } catch (error) {
    logger.error(`Failed to fetch transaction ${transactionId}:`, error)

    if (error.response?.status === 401) {
      logger.warn('Authentication failed when fetching transaction')
      return null
    }

    if (error.response?.status === 404) {
      throw new Error('交易不存在')
    }

    throw error
  }
}

/**
 * Create a new transaction
 * @param {Object} transactionData - The transaction data to create
 * @returns {Promise} - Promise that resolves to the created transaction
 */
export async function createTransaction(transactionData) {
  try {
    // Use different endpoints based on transaction type
    let endpoint = '/transactions/'
    if (transactionData.transaction_type === '收款') {
      endpoint = '/transactions/payment'
    } else if (transactionData.transaction_type === '代收代付') {
      endpoint = '/transactions/collection'
    }

    // Debug logging to verify endpoint selection
    logger.log('=== TRANSACTION CREATION DEBUG ===')
    logger.log('[TransactionService] Creating transaction with type:', transactionData.transaction_type)
    logger.log('[TransactionService] Selected endpoint:', endpoint)
    logger.log('[TransactionService] Transaction data:', transactionData)
    logger.log('==================================')

    const response = await axios.post(endpoint, transactionData)
    logger.log('[TransactionService] Create transaction response:', response)
    return response.data
  } catch (error) {
    logger.error('Failed to create transaction:', error)

    if (error.response?.status === 401) {
      throw new Error('您没有权限创建交易')
    }

    if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || '数据验证失败')
    }

    if (error.response?.status === 404) {
      throw new Error(error.response.data?.message || '客户或房源不存在')
    }

    throw error
  }
}

/**
 * Update an existing transaction
 * @param {number} transactionId - The transaction ID
 * @param {Object} transactionData - The updated transaction data
 * @returns {Promise} - Promise that resolves to the updated transaction
 */
export async function updateTransaction(transactionId, transactionData) {
  try {
    const response = await axios.put(`/transactions/${transactionId}`, transactionData)
    return response.data
  } catch (error) {
    logger.error(`Failed to update transaction ${transactionId}:`, error)

    if (error.response?.status === 401) {
      throw new Error('您没有权限更新交易')
    }

    if (error.response?.status === 404) {
      throw new Error('交易不存在')
    }

    throw error
  }
}

/**
 * Delete a transaction
 * @param {number} transactionId - The transaction ID
 * @returns {Promise} - Promise that resolves to deletion confirmation
 */
export async function deleteTransaction(transactionId) {
  try {
    const response = await axios.delete(`/transactions/${transactionId}`)
    return response.data
  } catch (error) {
    logger.error(`Failed to delete transaction ${transactionId}:`, error)

    if (error.response?.status === 401) {
      throw new Error('您没有权限删除交易')
    }

    if (error.response?.status === 404) {
      throw new Error('交易不存在')
    }

    throw error
  }
}

/**
 * Get transaction stages
 * @returns {Promise} - Promise that resolves to transaction stages array
 */
export async function getTransactionStages() {
  try {
    const response = await axios.get('/transactions/stages')
    return response.data.stages || []
  } catch (error) {
    logger.error('Failed to fetch transaction stages:', error)

    if (error.response?.status === 401) {
      logger.warn('Authentication failed when fetching transaction stages')
      return []
    }

    return []
  }
}

/**
 * Update transaction stage
 * @param {number} transactionId - The transaction ID
 * @param {Object} stageData - Stage update data
 * @returns {Promise} - Promise that resolves to updated transaction
 */
export async function updateTransactionStage(transactionId, stageData) {
  try {
    const response = await axios.put(`/transactions/${transactionId}/stage`, stageData)
    return response.data
  } catch (error) {
    logger.error(`Failed to update transaction ${transactionId} stage:`, error)

    if (error.response?.status === 401) {
      throw new Error('您没有权限更新交易阶段')
    }

    if (error.response?.status === 404) {
      throw new Error('交易或交易阶段不存在')
    }

    throw error
  }
}

/**
 * Get transaction statistics
 * @param {Object} filters - Date range filters
 * @returns {Promise} - Promise that resolves to statistics data
 */
export async function getTransactionStatistics(filters = {}) {
  try {
    const params = {}
    if (filters.dateFrom) params.date_from = filters.dateFrom
    if (filters.dateTo) params.date_to = filters.dateTo

    const response = await axios.get('/transactions/statistics', { params })
    return response.data
  } catch (error) {
    logger.error('Failed to fetch transaction statistics:', error)

    if (error.response?.status === 401) {
      logger.warn('Authentication failed when fetching transaction statistics')
      return {}
    }

    return {}
  }
}

/**
 * Review transaction (approve/reject)
 * @param {number} transactionId - The transaction ID
 * @param {string} action - 'approve' or 'reject'
 * @param {string} comment - Optional review comment
 * @returns {Promise} - Promise that resolves to updated transaction
 */
export async function reviewTransaction(transactionId, action, comment = '') {
  try {
    const response = await axios.put(`/transactions/${transactionId}/review`, {
      action,
      comment
    })
    return response.data
  } catch (error) {
    logger.error(`Failed to review transaction ${transactionId}:`, error)

    if (error.response?.status === 401) {
      throw new Error('您没有权限审核交易')
    }

    if (error.response?.status === 404) {
      throw new Error('交易不存在')
    }

    throw error
  }
}

/**
 * Get transactions for a specific property
 * @param {number} propertyId - The property ID
 * @param {Object} options - Additional options like transaction_type
 * @returns {Promise} - Promise that resolves to transactions array
 */
export async function getPropertyTransactions(propertyId, options = {}) {
  try {
    const params = {
      property_id: propertyId,
      sort_by: 'created_at',
      sort_order: 'desc'
    }

    if (options.transactionType) {
      params.transaction_type = options.transactionType
    }

    if (options.limit) {
      params.per_page = options.limit
    }

    const response = await fetchTransactions(params)
    return response.transactions || []
  } catch (error) {
    logger.error(`Failed to fetch transactions for property ${propertyId}:`, error)
    return []
  }
}

/**
 * Get transactions for a specific customer
 * @param {number} customerId - The customer ID
 * @param {Object} options - Additional options like transaction_type
 * @returns {Promise} - Promise that resolves to transactions array
 */
export async function getCustomerTransactions(customerId, options = {}) {
  try {
    const params = {
      customer_id: customerId,
      sort_by: 'created_at',
      sort_order: 'desc'
    }

    if (options.transactionType) {
      params.transaction_type = options.transactionType
    }

    if (options.limit) {
      params.per_page = options.limit
    }

    const response = await fetchTransactions(params)
    return response.transactions || []
  } catch (error) {
    logger.error(`Failed to fetch transactions for customer ${customerId}:`, error)
    return []
  }
}